import { useQuery } from "@apollo/client/react";
import { SIDE_BAR_LIMIT } from "config/appConfig";
import NetworkStateContext from "contexts/NetworkStateContext";
import WorkOrderSearchContext from "contexts/WorkOrderSearchContext";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import { useSchedulerState } from "hooks/state";
import useBryntumScheduler from "hooks/useBryntumScheduler";
import { useContext } from "react";
import Uuid from "utils/Uuid";
import buildFilterQueryParameters from "./helpers/buildFilterQueryParameters";
import { GetWorkOrders } from "./workOrder.gql";

let lastQueryId = 0;

const debouncedFetchMore = ({ variables, fetchMore }) => {
  const queryId = Uuid.generate();
  lastQueryId = queryId;

  return fetchMore({
    updateQuery: (previousResult, result) => {
      if (!result.fetchMoreResult || lastQueryId !== queryId) {
        return previousResult;
      }

      return result.fetchMoreResult;
    },
    variables,
  });
};

export default function useGetWorkOrders() {
  const applicationContext = useApplicationContext();
  const { getBryntumScheduler } = useBryntumScheduler();
  const {
    state: { visibleTimeRange },
  } = useSchedulerState();

  const { trackLoadingState, isFetching } = useContext(NetworkStateContext);

  const buildQueryParameters = (innerSearchState) =>
    buildFilterQueryParameters({
      applicationContext,
      bryntumScheduler: getBryntumScheduler(),
      visibleTimeRange,
      ...innerSearchState,
      limit: SIDE_BAR_LIMIT,
    });

  const { state: searchState } = useContext(WorkOrderSearchContext);

  const { data, fetchMore, loading, error } = useQuery(GetWorkOrders, {
    notifyOnNetworkStatusChange: true,
    variables: buildQueryParameters(searchState),
  });

  const doFetchMore = ({
    searchText,
    searchCategory,
    filters,
    visibleTimeRange: newDataTimeRange = visibleTimeRange,
  }) => {
    trackLoadingState("GET_WORK_ORDERS", () => {
      const variables = buildQueryParameters({
        filters,
        searchCategory,
        searchText,
        visibleTimeRange: newDataTimeRange,
      });

      return debouncedFetchMore({ fetchMore, variables });
    });
  };

  return {
    data,
    error,
    fetch: doFetchMore,
    fetching: isFetching("GET_WORK_ORDERS"),
    loading,
    workOrders: data?.workOrders,
  };
}
